import React, {PureComponent} from 'react';
import {NotificationManager} from 'react-notifications';

import * as api from '../../lib/api';
import * as validation from '../../lib/validation';
import {withUser} from '../../lib/user';
import Form from '../../components/Form';
import Input from '../../components/Input';
import ChipInput from '../../components/ChipInput';
import DateTimeInput from '../../components/DateTimeInput';
import Button from '../../components/Button';
import SideModal from '../../components/SideModal';

class ReminderFormModal extends PureComponent {
  state = this.getInitialState();

  getInitialState() {
    return {
      saving: false,
      validationsEnabled: false,
      id: undefined,
      ...(this.props.reminder || createReminder())
    };
  }

  componentDidUpdate(prevProps) {
    const {reminder} = this.props;

    if (prevProps.reminder !== reminder) {
      this.setState(this.getInitialState());
    }
  }

  handleSave = async () => {
    const {credentials, onSave} = this.props;
    const {saving, validationsEnabled, ...reminder} = this.state;
    const validationErrors = getValidationErrors(reminder);

    // Make sure the reminder passes validation.
    if (hasValidationErrors(validationErrors)) {
      this.setState({validationsEnabled: true});
      return;
    }

    this.setState({saving: true});

    try {
      const method = reminder.id ? 'updateReminder' : 'createReminder';
      const response = await api[method](reminder, credentials);

      NotificationManager.success('Your reminder was saved');
      onSave({...reminder, id: response.id});
    }
    catch(e) {
      NotificationManager.error('Unable to save your reminder. Please try again');
    }
    finally {
      this.setState(this.getInitialState());
    }
  }

  render() {
    const {open, onClose, groups} = this.props;
    const {saving, validationsEnabled, ...reminder} = this.state;
    const validationErrors = validationsEnabled ? getValidationErrors(reminder) : {};

    return (
      <Form onSubmit={this.handleSave}>
        <SideModal
          open={open}
          onClose={onClose}
          title={open && (reminder.id ? 'Edit Reminder' : 'Create Reminder')}
          options={[
            <Button type="submit" disabled={saving || hasValidationErrors(validationErrors)}>
              {saving ? 'Saving...' : 'Save Reminder'}
            </Button>,
            <Button secondary onClick={onClose}>Cancel</Button>
          ]}>
          {open && (
            <SideModal.Content>
              <Form.Group>
                <Input
                  required
                  autoFocus
                  id="reminder-message"
                  type="text"
                  label="Reminder"
                  value={reminder.message}
                  onChange={e => this.setState({message: e.target.value})}
                  helpText="What do you want to remind your team to do?"
                  invalid={validationErrors.message}
                  validationText={validationErrors.message}
                />
              </Form.Group>
              <Form.Group>
                <ChipInput
                  required={reminder.reminderType === 'EMAIL'}
                  id="reminder-groups"
                  label="User Groups"
                  placeholder="Start typing a group name..."
                  helpText="Which groups should get this reminder? Leave blank for everyone"
                  suggestions={groups}
                  value={reminder.groups}
                  onChange={groups => this.setState({groups})}
                  invalid={validationErrors.groups}
                  validationText={validationErrors.groups}
                />
              </Form.Group>
              <Form.Group>
                <Input
                  required
                  id="reminder-type"
                  type="select"
                  label="Delivery Method"
                  value={reminder.reminderType}
                  onChange={e => this.setState({reminderType: e.target.value})}
                  helpText="How should this reminder be sent?">
                  <option value="MOBILE">Mobile Push Notification</option>
                  <option value="EMAIL">Email Notification</option>
                </Input>
              </Form.Group>
              <Form.Group>
                <Input
                  required
                  id="reminder-interval"
                  type="select"
                  label="Reminder Frequency"
                  value={reminder.repeatInterval || ''}
                  onChange={e => this.setState({repeatInterval: e.target.value ? e.target.value : null})}
                  helpText="How often should this reminder be sent?">
                  <option value="">Once</option>
                  <option value="HOUR">Every hour</option>
                  <option value="DAY">Every day</option>
                  <option value="WEEK">Every week</option>
                </Input>
              </Form.Group>
              <Form.Group>
                <DateTimeInput
                  required
                  id="reminder-schedule-time"
                  label={reminder.repeatInterval ? 'Start Time' : 'Time'}
                  placeholder="Choose a date &amp; time..."
                  helpText="When should this reminder be sent?"
                  date={reminder.scheduleTime && new Date(reminder.scheduleTime)}
                  onChange={date => date && this.setState({scheduleTime: date.getTime()})}
                  invalid={validationErrors.scheduleTime}
                  validationText={validationErrors.scheduleTime}
                />
              </Form.Group>
            </SideModal.Content>
          )}
        </SideModal>
      </Form>
    );
  }
}

function createReminder() {
  return {
    message: '',
    scheduleTime: null,
    repeatInterval: null,
    groups: [],
    reminderType: 'MOBILE'
  };
}

const isValuePresent = validation.create((value, {isEmpty, trim}) => !isEmpty(trim(value)));

function getValidationErrors(reminder) {
  return {
    message: isValuePresent(reminder.message) ? null : 'A reminder is required.',
    groups: reminder.reminderType === 'EMAIL' && reminder.groups.length < 1 ? 'Email reminders require at least 1 group.' : null,
    scheduleTime: reminder.scheduleTime === null ? 'Please choose a date & time for this reminder.' : null
  };
}

function hasValidationErrors(errors) {
  return Object.keys(errors).filter(key => errors[key] != null).length > 0;
}

export default withUser(ReminderFormModal);